// @dynamic-i18n-keys
//   widgets.exhibitionist.source.featured_gallery
//   widgets.exhibitionist.source.newest
//   widgets.exhibitionist.source.gallery
//   widgets.exhibitionist.source.collection
//   widgets.exhibitionist.source.private_collection
//   widgets.exhibitionist.source.adoptable_bought
//   widgets.exhibitionist.source.adoptable_for_sale
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiModuleExhibitionist } from '@wix/da-papi-types';
import WidgetBox, {
  Props as WidgetBoxProps,
} from '@wix/da-gruser-shared/pkg/components/WidgetBox';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import useModuleData from '@wix/da-gruser-shared/pkg/hooks/useModuleData';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import usePage from '@wix/da-gruser-shared/pkg/hooks/usePage';
import PremiumFolderDataIcon from '../../../GallectionFolderDropdown/_partials/PremiumFolderDataIcon';
import ExhibitionistStrip from '../ExhibitionistStrip';
import ExhibitionistGrid from '../ExhibitionistGrid';
import ExhibitionistEmpty from '../ExhibitionistEmpty';
import {
  GallectionType,
  gallectionTypeToResourceType,
} from '../../../types/gallection';
import FavouritesLabel from '../_partials/FavouritesLabel';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import ConditionalWrapper from '@wix/da-ds/pkg/ConditionalWrapper/ConditionalWrapper';
import WidgetPaywall from '../../lego/WidgetPaywall';
import { ExhibitionistViewMode } from '../ExhibitionistConfigPopover/types';
import ExhibitionistSlideshow from '../ExhibitionistSlideshow';
import DuperbrowseContext from '../../../Duperbrowse/DeviationDuperbrowseContext';
import { EXHIBITIONIST_STRIP_DUPERBROWSE_STREAMID } from '../consts';
import { useExhibitionistType } from '../hooks';
import { ExhibitionistWidgetType } from '../types';
import { useShowCoreUpsell } from '../../hooks/useShowCoreUpsell';
import ExhibitionistGrid3x3 from '../ExhibitionistGrid3x3/ExhibitionistGrid3x3';

import s from './ExhibitionistBox.scss';

interface Props {
  moduleId: number;
  widgetBoxProps?: Partial<WidgetBoxProps>;
}

const ExhibitionistBox: React.FC<Props> = ({ moduleId, widgetBoxProps }) => {
  const { t } = useTranslation();
  const exhibitionistType = useExhibitionistType();
  const isMobile = useContext(MobileContext);
  const gruserPage = usePage();

  const { moduleData } = useModuleData<PapiModuleExhibitionist>(moduleId);
  const {
    title,
    sourceType,
    seeAllUrl,
    premiumFolderData,
    deviations,
    folderId,
    viewMode,
  } = moduleData;
  const isEmpty = deviations.length === 0;

  const showCoreUpsell =
    useShowCoreUpsell(moduleId) &&
    exhibitionistType === ExhibitionistWidgetType.STRIP_SHORT;

  const renderContent = () => {
    if (isEmpty) {
      return <ExhibitionistEmpty moduleId={moduleId} />;
    }

    let content;

    if (isMobile) {
      content = <ExhibitionistGrid3x3 moduleId={moduleId} />;
    } else if (exhibitionistType === ExhibitionistWidgetType.TALL) {
      const isSlideshow = viewMode === ExhibitionistViewMode.SLIDESHOW;
      content = isSlideshow ? (
        <ExhibitionistSlideshow moduleId={moduleId} />
      ) : (
        <ExhibitionistGrid moduleId={moduleId} />
      );
    } else {
      content = <ExhibitionistStrip moduleId={moduleId} />;
    }

    return (
      <DuperbrowseContext
        streamId={EXHIBITIONIST_STRIP_DUPERBROWSE_STREAMID}
        deviations={deviations}
      >
        {content}
      </DuperbrowseContext>
    );
  };

  return (
    <BiLoggerContextProvider
      value={{
        itemid: folderId || undefined,
        typeid: gallectionTypeToResourceType(sourceType),
      }}
    >
      <WidgetBox
        headerClassName={s['header']}
        header={
          <>
            <PremiumFolderDataIcon
              premiumFolderData={premiumFolderData}
              size={IconSize.COMPACT}
              className={s['premium-folder-icon']}
            />
            {title || t(`widgets.exhibitionist.source.${sourceType}`)}
            {sourceType === GallectionType.COLLECTION ? (
              <FavouritesLabel className={s['header-label']} />
            ) : undefined}
          </>
        }
        seeAllUrl={seeAllUrl}
        moduleId={moduleId}
        {...widgetBoxProps}
      >
        <ConditionalWrapper
          condition={showCoreUpsell}
          wrap={child => (
            <WidgetPaywall
              lockedClassName={s['widget-paywall-locked']}
              title={t('widgets.exhibitionist.coreOnly.paywall.title')}
              subtitle={t('widgets.exhibitionist.coreOnly.paywall.subtitle')}
              buttonAlignment="horizontal"
              previewReferral={
                gruserPage?.name === 'deviation'
                  ? ReferralType.DEVIATION_WIDGET_PAYWALL_PREVIEW
                  : ReferralType.WIDGET_PAYWALL_PREVIEW
              }
              lockedReferral={
                gruserPage?.name === 'deviation'
                  ? ReferralType.DEVIATION_WIDGET_PAYWALL_OVERLAY
                  : ReferralType.WIDGET_PAYWALL_OVERLAY
              }
            >
              {child}
            </WidgetPaywall>
          )}
        >
          {renderContent()}
        </ConditionalWrapper>
      </WidgetBox>
    </BiLoggerContextProvider>
  );
};
ExhibitionistBox.displayName = 'ExhibitionistBox';

export default ExhibitionistBox;
